/**
 * Feature Gating Configuration
 *
 * Defines which features are available at each subscription tier.
 * Used by the hasFeature() and getFeatureLimit() helpers to enforce access.
 *
 * Numeric values: -1 = unlimited, 0 = locked, positive = monthly limit
 * Boolean values: true = available, false = locked
 */

import type { StartupTier, InvestorTier, SubscriptionTier } from './types';

// ============================================================================
// Feature Keys
// ============================================================================

/**
 * Startup feature keys - used for type-safe feature checking
 */
export type StartupFeatureKey =
  // Core analysis (all tiers)
  | 'pitch_deck_analysis'
  | 'overall_readiness_score'
  | 'confidence_risk_rating'
  | 'top_3_recommendations'
  // Starter+ features
  | 'full_dimension_report'
  | 'data_room_analysis'
  | 'dd_conversations'
  | 'scoring_refresh'
  | 'valuation_basic'
  | 'valuation_full'
  // Access features
  | 'team_seats'
  | 'view_investor_database'
  | 'investor_activity_insights'
  // Communication
  | 'direct_messaging'
  // Premium features
  | 'automatic_investor_matching'
  | 'investor_feedback_loop'
  | 'priority_visibility';

/**
 * Investor feature keys - used for type-safe feature checking
 */
export type InvestorFeatureKey =
  // Inbox (all tiers)
  | 'inbox'
  | 'basic_scoring'
  | 'basic_filters'
  | 'watchlist'
  // Analysis (Starter+)
  | 'summary_dd_reports'
  | 'full_dimension_dd'
  | 'dd_conversations'
  | 'thesis_fit'
  // Access (Starter+)
  | 'platform_dealflow'
  // Communication
  | 'direct_messaging'
  | 'feedback_to_founders'
  // Management (Standard+)
  | 'deal_pipeline_view'
  | 'portfolio_tracking'
  | 'advanced_filters'
  // Premium
  | 'priority_deal_access'
  | 'auto_matching'
  | 'syndicate_tools';

export type FeatureKey = StartupFeatureKey | InvestorFeatureKey;

// ============================================================================
// Feature Access Type
// ============================================================================

type FeatureAccess = boolean | number; // boolean for on/off, number for limits (-1 = unlimited, 0 = locked)

// ============================================================================
// Startup Feature Matrix
// ============================================================================

/**
 * Startup features by tier
 *
 * | Feature                      | Free | Starter | Standard | Premium |
 * |------------------------------|:----:|:-------:|:--------:|:-------:|
 * | Pitch deck analysis          |  1   |    3    |   10     |   -1    |
 * | Overall readiness score      |  T   |    T    |    T     |    T    |
 * | Confidence + Risk rating     |  T   |    T    |    T     |    T    |
 * | Top 3 recommendations        |  T   |    T    |    T     |    T    |
 * | Full 15-dimension report     |  F   |    T    |    T     |    T    |
 * | Data room analysis           |  F   |    T    |    T     |    T    |
 * | DD conversations             |  0   |    3    |   10     |   -1    |
 * | Scoring refresh              |  0   |    1    |    3     |   -1    |
 * | Basic valuation (Berkus)     |  T   |    T    |    T     |    T    |
 * | Full valuation (4 methods)   |  F   |    T    |    T     |    T    |
 * | Team seats                   |  1   |    2    |    4     |    6    |
 * | View investor database       |  F   |    F    |    T     |    T    |
 * | Investor activity insights   |  F   |    F    |    T     |    T    |
 * | Direct messaging             |  0   |    0    |    5     |   -1    |
 * | Automatic investor matching  |  F   |    F    |    F     |    T    |
 * | Investor feedback loop       |  F   |    F    |    F     |    T    |
 * | Priority visibility          |  F   |    F    |    F     |    T    |
 */
export const STARTUP_FEATURES: Record<StartupTier, Record<StartupFeatureKey, FeatureAccess>> = {
  free: {
    pitch_deck_analysis: 1,
    overall_readiness_score: true,
    confidence_risk_rating: true,
    top_3_recommendations: true,
    full_dimension_report: false,
    data_room_analysis: false,
    dd_conversations: 0,
    scoring_refresh: 0,
    valuation_basic: true,
    valuation_full: false,
    team_seats: 1,
    view_investor_database: false,
    investor_activity_insights: false,
    direct_messaging: 0,
    automatic_investor_matching: false,
    investor_feedback_loop: false,
    priority_visibility: false,
  },
  starter: {
    pitch_deck_analysis: 3,
    overall_readiness_score: true,
    confidence_risk_rating: true,
    top_3_recommendations: true,
    full_dimension_report: true,
    data_room_analysis: true,
    dd_conversations: 3,
    scoring_refresh: 1,
    valuation_basic: true,
    valuation_full: true,
    team_seats: 2,
    view_investor_database: false,
    investor_activity_insights: false,
    direct_messaging: 0,
    automatic_investor_matching: false,
    investor_feedback_loop: false,
    priority_visibility: false,
  },
  standard: {
    pitch_deck_analysis: 10,
    overall_readiness_score: true,
    confidence_risk_rating: true,
    top_3_recommendations: true,
    full_dimension_report: true,
    data_room_analysis: true,
    dd_conversations: 10,
    scoring_refresh: 3,
    valuation_basic: true,
    valuation_full: true,
    team_seats: 4,
    view_investor_database: true,
    investor_activity_insights: true,
    direct_messaging: 5,
    automatic_investor_matching: false,
    investor_feedback_loop: false,
    priority_visibility: false,
  },
  premium: {
    pitch_deck_analysis: -1,
    overall_readiness_score: true,
    confidence_risk_rating: true,
    top_3_recommendations: true,
    full_dimension_report: true,
    data_room_analysis: true,
    dd_conversations: -1,
    scoring_refresh: -1,
    valuation_basic: true,
    valuation_full: true,
    team_seats: 6,
    view_investor_database: true,
    investor_activity_insights: true,
    direct_messaging: -1,
    automatic_investor_matching: true,
    investor_feedback_loop: true,
    priority_visibility: true,
  },
};

// ============================================================================
// Investor Feature Matrix
// ============================================================================

/**
 * Investor features by tier
 *
 * | Feature                 | Free | Starter | Standard | Premium |
 * |-------------------------|:----:|:-------:|:--------:|:-------:|
 * | Inbox                   |  T   |    T    |    T     |    T    |
 * | Basic scoring           | 50   |  100    |  300     |   -1    |
 * | Basic filters           |  T   |    T    |    T     |    T    |
 * | Watchlist               | 10   |   50    |  200     |   -1    |
 * | Summary DD reports      |  F   |    T    |    T     |    T    |
 * | Full dimension DD       |  F   |    T    |    T     |    T    |
 * | DD conversations        |  0   |    5    |   20     |   -1    |
 * | Thesis fit              |  F   |    F    |    T     |    T    |
 * | Platform dealflow       |  F   |    T    |    T     |    T    |
 * | Direct messaging        |  0   |   10    |   50     |   -1    |
 * | Feedback to founders    |  F   |    F    |    T     |    T    |
 * | Deal pipeline view      |  F   |    F    |    T     |    T    |
 * | Portfolio tracking      |  F   |    F    |    T     |    T    |
 * | Advanced filters        |  F   |    F    |    T     |    T    |
 * | Priority deal access    |  F   |    F    |    F     |    T    |
 * | Auto-matching           |  F   |    F    |    F     |    T    |
 * | Syndicate tools         |  F   |    F    |    F     |    T    |
 *
 * Note: basic_scoring is a MONTHLY cap for ALL tiers (free = 50/month — the
 * startup-acquisition flywheel: cheap for investors, populates the startup DB).
 * A deck dropped for a startup already scored recently reuses that score for free
 * (no credit spent) — see investor-analyze-deck.
 */
export const INVESTOR_FEATURES: Record<InvestorTier, Record<InvestorFeatureKey, FeatureAccess>> = {
  free: {
    inbox: true,
    basic_scoring: 50,
    basic_filters: true,
    watchlist: 10,
    summary_dd_reports: false,
    full_dimension_dd: false,
    dd_conversations: 0,
    thesis_fit: false,
    platform_dealflow: false,
    direct_messaging: 0,
    feedback_to_founders: false,
    deal_pipeline_view: false,
    portfolio_tracking: false,
    advanced_filters: false,
    priority_deal_access: false,
    auto_matching: false,
    syndicate_tools: false,
  },
  starter: {
    inbox: true,
    basic_scoring: 100,
    basic_filters: true,
    watchlist: 50,
    summary_dd_reports: true,
    full_dimension_dd: true,
    dd_conversations: 5,
    thesis_fit: false,
    platform_dealflow: true,
    direct_messaging: 10,
    feedback_to_founders: false,
    deal_pipeline_view: false,
    portfolio_tracking: false,
    advanced_filters: false,
    priority_deal_access: false,
    auto_matching: false,
    syndicate_tools: false,
  },
  standard: {
    inbox: true,
    basic_scoring: 300,
    basic_filters: true,
    watchlist: 200,
    summary_dd_reports: true,
    full_dimension_dd: true,
    dd_conversations: 20,
    thesis_fit: true,
    platform_dealflow: true,
    direct_messaging: 50,
    feedback_to_founders: true,
    deal_pipeline_view: true,
    portfolio_tracking: true,
    advanced_filters: true,
    priority_deal_access: false,
    auto_matching: false,
    syndicate_tools: false,
  },
  premium: {
    inbox: true,
    basic_scoring: -1,
    basic_filters: true,
    watchlist: -1,
    summary_dd_reports: true,
    full_dimension_dd: true,
    dd_conversations: -1,
    thesis_fit: true,
    platform_dealflow: true,
    direct_messaging: -1,
    feedback_to_founders: true,
    deal_pipeline_view: true,
    portfolio_tracking: true,
    advanced_filters: true,
    priority_deal_access: true,
    auto_matching: true,
    syndicate_tools: true,
  },
};

// ============================================================================
// Feature Metadata
// ============================================================================

export interface FeatureInfo {
  key: FeatureKey;
  name: string;
  description: string;
  category: 'analysis' | 'access' | 'communication' | 'advanced' | 'inbox' | 'management';
}

/**
 * Human-readable feature information for UI display
 */
export const FEATURE_INFO: Record<FeatureKey, FeatureInfo> = {
  // Startup features
  pitch_deck_analysis: {
    key: 'pitch_deck_analysis',
    name: 'Pitch Deck Analysis',
    description: 'AI-powered analysis of your pitch deck',
    category: 'analysis',
  },
  overall_readiness_score: {
    key: 'overall_readiness_score',
    name: 'Readiness Score',
    description: 'Overall investment readiness score (0-100)',
    category: 'analysis',
  },
  confidence_risk_rating: {
    key: 'confidence_risk_rating',
    name: 'Confidence & Risk Rating',
    description: 'Data confidence level and risk assessment',
    category: 'analysis',
  },
  top_3_recommendations: {
    key: 'top_3_recommendations',
    name: 'Top 3 Recommendations',
    description: 'Key areas to improve your score',
    category: 'analysis',
  },
  full_dimension_report: {
    key: 'full_dimension_report',
    name: 'Full 15-Dimension Report',
    description: 'Comprehensive scoring across all investment criteria',
    category: 'analysis',
  },
  data_room_analysis: {
    key: 'data_room_analysis',
    name: 'Data Room Analysis',
    description: 'Document completeness and quality assessment',
    category: 'analysis',
  },
  dd_conversations: {
    key: 'dd_conversations',
    name: 'DD Conversations',
    description: 'AI-powered due diligence conversations',
    category: 'analysis',
  },
  scoring_refresh: {
    key: 'scoring_refresh',
    name: 'Scoring Refresh',
    description: 'Re-score with updated data',
    category: 'analysis',
  },
  valuation_basic: {
    key: 'valuation_basic',
    name: 'Basic Valuation',
    description: 'Berkus Method startup valuation estimate',
    category: 'analysis',
  },
  valuation_full: {
    key: 'valuation_full',
    name: 'Full Valuation Analysis',
    description: 'All 4 valuation methods with comparison chart',
    category: 'analysis',
  },
  team_seats: {
    key: 'team_seats',
    name: 'Team Seats',
    description: 'Team member accounts (including founder)',
    category: 'access',
  },
  view_investor_database: {
    key: 'view_investor_database',
    name: 'Investor Database',
    description: 'Browse and filter potential investors',
    category: 'access',
  },
  investor_activity_insights: {
    key: 'investor_activity_insights',
    name: 'Investor Activity Insights',
    description: 'See which investors viewed your profile',
    category: 'access',
  },
  direct_messaging: {
    key: 'direct_messaging',
    name: 'Direct Messaging',
    description: 'Send messages to investors or founders',
    category: 'communication',
  },
  automatic_investor_matching: {
    key: 'automatic_investor_matching',
    name: 'Auto-Matching',
    description: 'AI-powered investor recommendations',
    category: 'advanced',
  },
  investor_feedback_loop: {
    key: 'investor_feedback_loop',
    name: 'Investor Feedback',
    description: 'Receive feedback from investors who reviewed you',
    category: 'advanced',
  },
  priority_visibility: {
    key: 'priority_visibility',
    name: 'Priority Visibility',
    description: 'Appear higher in investor dealflow',
    category: 'advanced',
  },

  // Investor features
  inbox: {
    key: 'inbox',
    name: 'Inbox',
    description: 'Receive and triage pitch decks from founders',
    category: 'inbox',
  },
  basic_scoring: {
    key: 'basic_scoring',
    name: 'Basic Scoring',
    description: 'Score uploaded startup pitch decks',
    category: 'inbox',
  },
  basic_filters: {
    key: 'basic_filters',
    name: 'Basic Filters',
    description: 'Filter by stage, industry, and location',
    category: 'inbox',
  },
  watchlist: {
    key: 'watchlist',
    name: 'Watchlist',
    description: 'Bookmark startups to monitor until investment or pass',
    category: 'inbox',
  },
  summary_dd_reports: {
    key: 'summary_dd_reports',
    name: 'Summary DD Reports',
    description: 'Quick due diligence summaries',
    category: 'analysis',
  },
  full_dimension_dd: {
    key: 'full_dimension_dd',
    name: 'Full DD Reports',
    description: 'Complete 15-dimension due diligence',
    category: 'analysis',
  },
  thesis_fit: {
    key: 'thesis_fit',
    name: 'Thesis Fit Analysis',
    description: 'Personalized scoring based on your investment thesis weights',
    category: 'analysis',
  },
  platform_dealflow: {
    key: 'platform_dealflow',
    name: 'Platform Dealflow',
    description: 'Browse investment-ready startups on the marketplace',
    category: 'access',
  },
  feedback_to_founders: {
    key: 'feedback_to_founders',
    name: 'Founder Feedback',
    description: 'Send structured feedback to founders',
    category: 'communication',
  },
  deal_pipeline_view: {
    key: 'deal_pipeline_view',
    name: 'Deal Pipeline',
    description: 'Track deals through your investment process',
    category: 'management',
  },
  portfolio_tracking: {
    key: 'portfolio_tracking',
    name: 'Portfolio Tracking',
    description: 'Track and manage your investments',
    category: 'management',
  },
  advanced_filters: {
    key: 'advanced_filters',
    name: 'Advanced Filters',
    description: 'Filter by score, traction, and more',
    category: 'management',
  },
  priority_deal_access: {
    key: 'priority_deal_access',
    name: 'Priority Access',
    description: 'See new deals before other investors',
    category: 'advanced',
  },
  auto_matching: {
    key: 'auto_matching',
    name: 'Auto-Matching',
    description: 'Get matched with startups fitting your thesis',
    category: 'advanced',
  },
  syndicate_tools: {
    key: 'syndicate_tools',
    name: 'Syndicate Tools',
    description: 'Co-invest coordination and syndicate management',
    category: 'advanced',
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a startup tier has access to a feature
 */
export function startupHasFeature(
  tier: StartupTier,
  feature: StartupFeatureKey
): boolean {
  const access = STARTUP_FEATURES[tier]?.[feature];
  return typeof access === 'boolean' ? access : access !== 0;
}

/**
 * Check if an investor tier has access to a feature
 */
export function investorHasFeature(
  tier: InvestorTier,
  feature: InvestorFeatureKey
): boolean {
  const access = INVESTOR_FEATURES[tier]?.[feature];
  return typeof access === 'boolean' ? access : access !== 0;
}

/**
 * Get the numeric limit for a startup feature (e.g., team seats, DD conversations)
 */
export function getStartupFeatureLimit(
  tier: StartupTier,
  feature: StartupFeatureKey
): number {
  const access = STARTUP_FEATURES[tier]?.[feature];
  return typeof access === 'number' ? access : access ? 1 : 0;
}

/**
 * Get the numeric limit for an investor feature
 */
export function getInvestorFeatureLimit(
  tier: InvestorTier,
  feature: InvestorFeatureKey
): number {
  const access = INVESTOR_FEATURES[tier]?.[feature];
  return typeof access === 'number' ? access : access ? 1 : 0;
}

/**
 * Get the minimum tier required for a startup feature
 */
export function getMinimumStartupTier(feature: StartupFeatureKey): StartupTier {
  const tiers: StartupTier[] = ['free', 'starter', 'standard', 'premium'];
  for (const tier of tiers) {
    if (startupHasFeature(tier, feature)) {
      return tier;
    }
  }
  return 'premium'; // Feature doesn't exist, require highest tier
}

/**
 * Get the minimum tier required for an investor feature
 */
export function getMinimumInvestorTier(feature: InvestorFeatureKey): InvestorTier {
  const tiers: InvestorTier[] = ['free', 'starter', 'standard', 'premium'];
  for (const tier of tiers) {
    if (investorHasFeature(tier, feature)) {
      return tier;
    }
  }
  return 'premium'; // Feature doesn't exist, require highest tier
}

/**
 * Get all features that would be unlocked by upgrading from one tier to another
 */
export function getStartupUpgradeFeatures(
  fromTier: StartupTier,
  toTier: StartupTier
): StartupFeatureKey[] {
  const features = Object.keys(STARTUP_FEATURES.free) as StartupFeatureKey[];
  return features.filter(
    (feature) =>
      !startupHasFeature(fromTier, feature) && startupHasFeature(toTier, feature)
  );
}

/**
 * Get all features that would be unlocked by upgrading from one tier to another
 */
export function getInvestorUpgradeFeatures(
  fromTier: InvestorTier,
  toTier: InvestorTier
): InvestorFeatureKey[] {
  const features = Object.keys(INVESTOR_FEATURES.free) as InvestorFeatureKey[];
  return features.filter(
    (feature) =>
      !investorHasFeature(fromTier, feature) && investorHasFeature(toTier, feature)
  );
}

/**
 * Check if a feature has unlimited access at a given tier
 */
export function isUnlimited(
  tier: SubscriptionTier,
  feature: FeatureKey,
  userRole: 'startup' | 'investor'
): boolean {
  if (userRole === 'startup') {
    const access = STARTUP_FEATURES[tier as StartupTier]?.[feature as StartupFeatureKey];
    return access === -1;
  }
  const access = INVESTOR_FEATURES[tier as InvestorTier]?.[feature as InvestorFeatureKey];
  return access === -1;
}

/**
 * Format a feature limit for display
 * -1 -> "Unlimited", 0 -> null (locked), n -> "n" or "n/mo"
 */
export function formatFeatureLimit(
  value: number,
  suffix?: string
): string | null {
  if (value === -1) return 'Unlimited';
  if (value === 0) return null;
  return suffix ? `${value}${suffix}` : `${value}`;
}
